import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import useLocalizedNavigate from 'hooks/useLocalizedNavigate';
import { LockIcon } from 'components/icons';
import { useAppDispatch, useAppSelector } from 'store/hooks';
import { setLoginOpen } from 'store/uiSlice';
import Skeleton from 'components/skeleton/Skeleton';
import styles from './gameCard.module.scss';

export interface Game {
  id: string | number;
  name: string;
  provider?: string;
  imgUrl: string;
  playingCount: number;
  isFavorite: boolean;
}

interface GameCardProps {
  game: Game;
  hideFooter?: boolean;
  hideOverlayTop?: boolean;
  onPlayClick?: () => void;
}

const GameCard: React.FC<GameCardProps> = ({
  game,
  hideFooter = true,
  hideOverlayTop = false,
  onPlayClick,
}) => {
  const { t } = useTranslation();
  const navigate = useLocalizedNavigate();
  const dispatch = useAppDispatch();
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);
  const config = useAppSelector((state) => state.site.config);
  const isGeoBlocked = config?.geo?.isBlocked;

  const handlePlay = (isDemo: boolean) => {
    if (onPlayClick) {
      onPlayClick();
    }

    if (!isDemo && !isAuthenticated) {
      dispatch(setLoginOpen(true));
      return;
    }

    const slug = game.name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)+/g, '');
    navigate(`/game/casino/${slug}`, { state: { gameId: game.id, isDemo, imgUrl: game.imgUrl } });
  };

  const [prevImgUrl, setPrevImgUrl] = useState(game.imgUrl);
  const [imageStatus, setImageStatus] = useState<'loading' | 'loaded' | 'error'>('loading');

  if (game.imgUrl !== prevImgUrl) {
    setPrevImgUrl(game.imgUrl);
    setImageStatus('loading');
  }

  return (
    <div 
      className={styles.card} 
      onClick={() => !isGeoBlocked && handlePlay(!isAuthenticated)}
    >
      <div className={`${styles.imageContainer} ${imageStatus !== 'loading' ? styles.loaded : ''}`}>
        {imageStatus === 'loading' && (
          <Skeleton className={styles.imageSkeleton} variant="rectangular" animation="shimmer" />
        )}
        {imageStatus === 'error' ? (
          <div className={styles.fallbackContainer}>
            <span className={styles.fallbackText}>{game.name}</span>
          </div>
        ) : (
          <img
            src={game.imgUrl}
            alt={game.name}
            className={`${styles.image} ${imageStatus === 'loaded' ? styles.loaded : ''}`}
            onLoad={() => setImageStatus('loaded')}
            onError={() => setImageStatus('error')}
            loading="lazy"
          />
        )}

        {/* Hover Overlay */}
        <div className={styles.overlay}>
          {isGeoBlocked ? (
            <div className={styles.lockedOverlayContainer}>
              <LockIcon style={{ width: '48px', height: '48px' }} />
              <span>Unavailable</span>
            </div>
          ) : (
            <>
              <div className={styles.overlayCenter}>
                <h3 className={styles.gameName}>{game.name}</h3>
              </div>
              <div className={styles.overlayBottom}>
                <button 
                  className={styles.playBtn} 
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePlay(!isAuthenticated);
                  }}
                >
                  {t('common.play', 'PLAY')}
                </button>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Under-card info (Playing Count) */}
      {!hideFooter && (
        <div className={styles.footer}>
          <span className={styles.statusDot}></span>
          <span className={styles.playingText}>
            <strong>{game.playingCount}</strong> playing
          </span>
        </div>
      )}
    </div>
  );
};

export default GameCard;
